
import { Flight, FlightStatus, City } from "@/types/flight";
import { useState } from "react";
import { CityFlightModal } from "../CityFlightModal";
import { formatTime } from "@/lib/formatters";

const PNG_DOMESTIC_CODES = [
  "POM", "LAE", "HGU", "RAB", "MAG", "GUR", "BUA", "TBG", "GKA", "KIE", "UNG", "PNP", "BIG",
];

function isInternational(city?: City): boolean {
  if (!city?.code) return false;
  return !PNG_DOMESTIC_CODES.includes(city.code);
}

function getStatusColor(status: FlightStatus): string {
  switch (status) {
    case "Cancelled":
    case "Delayed":
      return "#FF4444";
    case "Boarding":
    case "Boarding Soon":
    case "Landed":
    case "Arrived":
      return "#44FF88";
    default:
      return "#FFFFFF";
  }
}

function getStatusLabel(status: FlightStatus): string {
  switch (status) {
    case "Arrived":
      return "Arrived";
    case "Landed":
      return "Now Landing";
    default:
      return status;
  }
}

interface FIDSFlightBoardProps {
  flights: Flight[];
  mode?: "all" | "departures" | "arrivals";
  searchTerm?: string;
  onFlightClick?: (flight: Flight) => void;
}

export function FIDSFlightBoard({ flights, mode = "all", searchTerm = "", onFlightClick }: FIDSFlightBoardProps) {
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCityClick = (e: React.MouseEvent, city: City) => {
    e.stopPropagation();
    setSelectedCity(city);
    setIsModalOpen(true);
  };

  const sortedFlights = [...flights].sort((a, b) => {
    return new Date(a.scheduled_departure).getTime() - new Date(b.scheduled_departure).getTime();
  });

  if (!flights || flights.length === 0) {
    return (
      <div className="w-full p-8 text-center" style={{ color: "#FFFFFF" }}>
        {searchTerm ? (
          <p style={{ color: "rgba(255,255,255,0.5)" }}>No flights matching "{searchTerm}"</p>
        ) : (
          <p>No flights available</p>
        )}
      </div>
    );
  }

  const showFrom = mode === "all" || mode === "arrivals";
  const showTo = mode === "all" || mode === "departures";

  return (
    <div className="w-full overflow-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr style={{ backgroundColor: "#0a0f2c" }}>
            <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase tracking-wider">Flight Number</th>
            {showFrom && (
              <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase tracking-wider">From</th>
            )}
            {showTo && (
              <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase tracking-wider">To</th>
            )}
            <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase tracking-wider">ETD</th>
            <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase tracking-wider">ETA</th>
            <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase tracking-wider">Gate</th>
            <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase tracking-wider">Status</th>
          </tr>
        </thead>
        <tbody>
          {sortedFlights.map((flight, index) => {
            const rowBg = index % 2 === 0 ? "#0d1b4b" : "#0a1540";
            const depIsIntl = isInternational(flight.departure_city);
            const arrIsIntl = isInternational(flight.arrival_city);

            return (
              <tr key={flight.id} style={{ backgroundColor: rowBg, cursor: onFlightClick ? "pointer" : undefined }} onClick={() => onFlightClick?.(flight)} className="hover:brightness-125 transition-all">
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-white font-bold">{flight.flight_number}</span>
                    <span className="text-white/60 text-xs">{flight.organization?.name}</span>
                  </div>
                </td>
                {showFrom && (
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => flight.departure_city && handleCityClick(e, flight.departure_city)}
                      className="text-left hover:underline cursor-pointer transition-colors"
                      disabled={!flight.departure_city}
                      style={{ color: depIsIntl ? "#FFD700" : "#FFFFFF" }}
                    >
                      <span className="font-bold">{flight.departure_city?.name}</span>
                      <span className="ml-1 opacity-70">({flight.departure_city?.code})</span>
                    </button>
                  </td>
                )}
                {showTo && (
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => flight.arrival_city && handleCityClick(e, flight.arrival_city)}
                      className="text-left hover:underline cursor-pointer transition-colors"
                      disabled={!flight.arrival_city}
                      style={{ color: arrIsIntl ? "#FFD700" : "#FFFFFF" }}
                    >
                      <span className="font-bold">{flight.arrival_city?.name}</span>
                      <span className="ml-1 opacity-70">({flight.arrival_city?.code})</span>
                    </button>
                  </td>
                )}
                <td className="px-4 py-3 text-white font-bold font-mono">
                  {flight.etd ? formatTime(flight.etd) : formatTime(flight.scheduled_departure)}
                </td>
                <td className="px-4 py-3 text-white font-bold font-mono">
                  {(() => {
                    const isArrived = flight.status === 'Landed' || flight.status === 'Arrived';
                    if (flight.eta) return formatTime(flight.eta);
                    if (flight.scheduled_arrival && flight.scheduled_arrival !== flight.scheduled_departure) {
                      return formatTime(flight.scheduled_arrival);
                    }
                    return <span style={{ color: "rgba(255,255,255,0.4)" }}>TBA</span>;
                  })()}
                </td>
                <td className="px-4 py-3 text-white font-bold">
                  {flight.gate || "TBA"}
                </td>
                <td className="px-4 py-3 font-bold" style={{ color: getStatusColor(flight.status) }}>
                  <span className="inline-flex items-center gap-1">
                    {getStatusLabel(flight.status)}
                    {flight.status_reason && flight.source === "cache" && (
                      <span title={flight.status_reason || "Manually updated"} className="cursor-help text-xs">✏️</span>
                    )}
                  </span>
                  {flight.status_reason && (
                    <div className="text-xs text-white/50 mt-0.5">{flight.status_reason}</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <CityFlightModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedCity(null); }}
        city={selectedCity}
      />
    </div>
  );
}
